<template>
    <div class="location-popup-container">
        <popup-modal @setAction="handleSetAction" @cancelAction="handleCancelAction">
            <div class="popup-wrapper">
                <div class="left">
                    <p class="title">Location</p>
                    <p class="subtitle">Enter your venue address</p>
                    <div class="venue-name-input">
                        <label for="venue-name">Venue name</label>
                        <input
                            v-model="formValues.venueName"
                            type="text"
                            name="venue-name"
                            maxlength="50"
                            placeholder="Enter your venue name"
                        />
                    </div>
                    <div class="location-input">
                        <input
                            ref="autocomplete"
                            type="text"
                            name="location"
                            placeholder="Type your address"
                        />
                    </div>
                    <div v-if="compErrMsg" class="input-err-msg">
                        {{ compErrMsg }}
                    </div>
                </div>
                <div class="right">
                    <div ref="google-map" id="google-map"></div>
                </div>
            </div>
        </popup-modal>
    </div>
</template>
<script>
    import axios from 'axios'
    import Google from '@/constants/google'
    import PopupModal from '@/components/user/login-popups/PopupModal.vue'

    const URLS = {
        user: '/profile/v1/settings/user/',
        info: '/profile/v1/info/venue/'
    }

    export default {
        name: 'Location',

        components: {
            'popup-modal': PopupModal
        },

        emits: ['closeModalAction'],

        data: () => ({
            compErrMsg: '',
            formValues: {
                venueName: '',
                location: '',
                lat: null,
                lng: null
            },
            mapData: {
                map: null,
                autocomplete: null,
                apiLoaded: false,
                checkInterval: null
            }
        }),

        methods: {
            // NOTE: this needs to be the same name as callback defined in GoogleMapsApiController.php
            initialize() {
                // eslint-disable-next-line
                this.mapData.map = new google.maps.Map(this.$refs['google-map'], Google.DEFAULT_MAP_OPT)

                // eslint-disable-next-line
                this.mapData.autocomplete = new google.maps.places.Autocomplete(
                    this.$refs['autocomplete'],
                    {
                        fields: ['geometry', 'formatted_address', 'name'],
                        types: ['geocode', 'establishment']
                    }
                )

                this.mapData.autocomplete.addListener('place_changed', this.onPlaceChanged)
            },

            onPlaceChanged() {
                let place = this.mapData.autocomplete.getPlace()

                if (place.geometry && place.formatted_address) {
                    let lat = place.geometry.location.lat()
                    let lng = place.geometry.location.lng()

                    this.mapData.map.panTo({ lat, lng })

                    this.formValues.location = place.formatted_address
                    this.formValues.lat = lat
                    this.formValues.lng = lng
                    this.formValues.venueName = this.getVenueName(
                        place.formatted_address,
                        place.name
                    )
                }
            },

            getVenueName(full_addr, name) {
                if (!name || !full_addr) return ''

                let streetComponent = full_addr.split(',')[0].split(' ').slice(-1)[0]

                if (name.includes(streetComponent)) return ''

                return name
            },

            prepInfoData() {
                let data = {
                    venue_name: this.formValues.venueName,
                    address: this.formValues.location,
                    lon: parseFloat(this.formValues.lng),
                    lat: parseFloat(this.formValues.lat)
                }

                return data
            },

            async handleSetAction() {
                this.compErrMsg = ''

                if (
                    this.formValues.location &&
                    this.formValues.lat &&
                    this.formValues.lng &&
                    this.formValues.venueName
                ) {
                    let infoData = this.prepInfoData()

                    await this.updateInfo(infoData)
                    this.$emit('closeModalAction')
                } else {
                    this.compErrMsg =
                        '[422] : You must select an address and fill out a venue name.'
                }
            },

            handleCancelAction() {
                for (const key of Object.keys(this.formValues)) {
                    this.$set(this.formValues, key, '')
                }

                this.compErrMsg = ''
                this.$emit('closeModalAction')
            },

            async updateInfo(data) {
                try {
                    let userResp = await axios.get(this.URLS.user)
                    let userId = userResp.data.data[0].id

                    let infoResp = await axios.post(this.URLS.info + userId, {
                        _method: 'PUT',
                        ...data
                    })
                    return infoResp
                } catch (err) {
                    this.compErrMsg = `[${err.response?.status}] : ${err.response?.data?.message}`
                    throw err
                }
            }
        },

        computed: {
            URLS: () => URLS
        },

        watch: {
            'mapData.apiLoaded': function (loaded) {
                if (loaded) {
                    clearInterval(this.mapData.checkInterval)
                }
            }
        },

        mounted() {
            this.mapData.checkInterval = setInterval(() => {
                if (window.google) {
                    this.mapData.apiLoaded = true
                    this.initialize()
                }
            })
        }
    }
</script>
<style lang="scss" scoped>
    .popup-wrapper {
        display: flex;
        flex-direction: row;
        justify-content: center;
        align-items: flex-start;
        margin-left: 90px;
        gap: 24px;

        .left {
            display: flex;
            flex-direction: column;

            .title {
                margin-top: 50px;
                margin-bottom: 0px;
                color: #dedede;
                font-size: 44px;
                font-family: Instrument Sans;
                font-style: normal;
                font-weight: 500;
                line-height: 110%;
                letter-spacing: -1.76px;
            }

            .subtitle {
                margin-top: 10px;
                margin-bottom: 0px;
                color: #acacac;
                font-size: 16px;
                font-family: Instrument Sans;
                font-style: normal;
                font-weight: 550;
                line-height: 125%;
            }

            .venue-name-input {
                margin-top: 25px;
            }

            .location-input {
                margin-top: 25px;
            }

            label {
                display: block;
                color: #8b8b8b;
                font-size: 20px;
                font-family: Instrument Sans;
                font-style: normal;
                font-weight: 550;
                line-height: 120%;
                letter-spacing: -0.2px;
                margin-bottom: 10px;
            }

            input {
                min-width: 458px;
                padding: 12px 55px 15px 15px;
                border-radius: 12px;
                border: 1.5px solid #8b8b8b;
                color: #8b8b8b;
                font-size: 20px;
                font-family: Instrument Sans;
                font-style: normal;
                font-weight: 550;
                line-height: 120%;
                letter-spacing: -0.2px;
                background-color: #262626;
            }

            input[name='location'] {
                background-image: url(/images/icons/search_icon_grey.svg);
                background-repeat: no-repeat;
                background-position: 413px;
            }

            input::placeholder {
                color: #8b8b8b;
                font-size: 20px;
                font-family: Instrument Sans;
                font-style: normal;
                font-weight: 550;
                line-height: 120%;
                letter-spacing: -0.2px;
            }
        }

        .right {
            #google-map {
                width: 498px;
                height: 300px;
                border-radius: 15px;
                margin-top: 159px;
            }
        }
    }
</style>
